import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { useInactivityConfig } from "@/hooks/use-inactivity-config";
import { useInactivityTimeout } from "@/hooks/use-inactivity-timeout";
import { AUTH_LOGOUT_REASON_KEY } from "@/services/auth/auth.service";
import { authStore } from "@/stores/auth-store";
import { logApp } from "@/utils/logApp";
import {
	InactivityWarningModal,
	useInactivityAnnouncements,
} from "./InactivityWarningModal";

/** Logout reason types for displaying appropriate messages */
export type LogoutReason = "inactivity" | "session" | "manual";

/**
 * Props for the InactivityTimeoutProvider
 */
export interface InactivityTimeoutProviderProps {
	/** Optional children (if you want to wrap other components) */
	children?: React.ReactNode;
	/** Override default configuration */
	configOverrides?: {
		warningTimeout?: number;
		logoutTimeout?: number;
		enabled?: boolean;
	};
	/** Custom callback when warning is triggered */
	onWarning?: () => void;
	/** Custom callback when logout occurs */
	onLogout?: () => void;
	/** Whether to log debug information */
	debug?: boolean;
}

/**
 * Provider component that manages the global inactivity timeout system
 *
 * This component:
 * - Monitors user activity across the entire application
 * - Shows warning modal when user approaches timeout
 * - Automatically logs out inactive users
 * - Integrates with existing auth and config systems
 * - Provides accessibility support
 *
 * Should be mounted once at the application root level.
 */
export function InactivityTimeoutProvider({
	children,
	configOverrides,
	onWarning,
	onLogout,
	debug = false,
}: InactivityTimeoutProviderProps) {
	const { isAuthenticated, isLoading } = useAuth();
	const config = useInactivityConfig();
	const [showWarningModal, setShowWarningModal] = useState(false);
	const [isClient, setIsClient] = useState(false);

	// Detect client-side rendering to prevent hydration mismatch
	useEffect(() => {
		setIsClient(true);
	}, []);

	// Merge config with any overrides
	const effectiveConfig = useMemo(
		() => ({
			...config,
			...configOverrides,
		}),
		[config, configOverrides],
	);

	// Debug logging
	useEffect(() => {
		if (debug) {
			console.log("🔍 InactivityTimeoutProvider: Config updated", {
				isAuthenticated,
				isLoading,
				config: effectiveConfig,
			});
		}
	}, [debug, isAuthenticated, isLoading, effectiveConfig]);

	// Handle warning phase
	const handleWarning = useCallback(() => {
		if (debug) {
			console.log("⚠️ InactivityTimeoutProvider: Warning phase triggered");
		}

		setShowWarningModal(true);
		onWarning?.();
	}, [debug, onWarning]);

	// Handle logout with reason
	const handleLogoutWithReason = useCallback(
		async (reason: LogoutReason) => {
			if (debug) {
				console.log(
					`🔒 InactivityTimeoutProvider: Logout triggered (reason: ${reason})`,
				);
			}

			// Hide modal first for better UX
			setShowWarningModal(false);

			// Call custom logout callback if provided
			onLogout?.();

			// Persist reason for /signed-out after Logto returns (post_logout_redirect_uri
			// is registered without a query string, so it cannot carry ?reason=).
			if (typeof window !== "undefined") {
				sessionStorage.setItem(AUTH_LOGOUT_REASON_KEY, reason);
			}

			// Perform actual logout via auth store (top-level navigation to the API).
			// Do not assign window.location to /signed-out here — that would skip Logto.
			try {
				await authStore.getState().logout();
			} catch (error) {
				console.error("❌ InactivityTimeoutProvider: Logout failed:", error);
				logApp({
					level: "error",
					message: `InactivityTimeoutProvider: Logout failed: ${error}`,
				});
				window.location.href = `/signed-out?reason=${reason}`;
			}
		},
		[debug, onLogout],
	);

	// Handle inactivity logout (automatic timeout)
	const handleLogout = useCallback(() => {
		handleLogoutWithReason("inactivity");
	}, [handleLogoutWithReason]);

	// Initialize the inactivity timeout system
	const inactivityState = useInactivityTimeout({
		warningTimeout: effectiveConfig.warningTimeout,
		logoutTimeout: effectiveConfig.logoutTimeout,
		enabled: effectiveConfig.enabled && isAuthenticated && !isLoading,
		onWarning: handleWarning,
		onLogout: handleLogout,
		events: effectiveConfig.events,
	});

	// Handle "Stay Logged In" action
	const handleStayLoggedIn = useCallback(() => {
		if (debug) {
			console.log("👤 InactivityTimeoutProvider: User chose to stay logged in");
		}

		setShowWarningModal(false);
		inactivityState.resetActivity();
	}, [debug, inactivityState]);

	// Handle "Log Out Now" action (user explicitly clicks logout)
	const handleLogoutNow = useCallback(() => {
		if (debug) {
			console.log("👤 InactivityTimeoutProvider: User chose to log out now");
		}

		// User clicked "Log Out Now" - this is still due to inactivity warning
		handleLogoutWithReason("inactivity");
	}, [debug, handleLogoutWithReason]);

	// Accessibility announcements
	const announcementElement = useInactivityAnnouncements(
		inactivityState.timeRemaining,
		inactivityState.isWarning,
	);

	// Only render inactivity features on client to prevent hydration mismatch
	if (!isClient) {
		return <>{children}</>;
	}

	// Don't render anything if not authenticated or still loading
	if (!isAuthenticated || isLoading) {
		return <>{children}</>;
	}

	// Don't render if system is disabled
	if (!effectiveConfig.enabled) {
		if (debug) {
			console.log("🚫 InactivityTimeoutProvider: System disabled");
		}
		return <>{children}</>;
	}

	return (
		<>
			{children}

			{/* Warning Modal */}
			<InactivityWarningModal
				isOpen={showWarningModal}
				timeRemaining={inactivityState.timeRemaining}
				onStayLoggedIn={handleStayLoggedIn}
				onLogout={handleLogoutNow}
				autoFocus={true}
			/>

			{/* Accessibility Announcements */}
			{announcementElement}

			{/* Debug Information (only in development) */}
			{debug && process.env.NODE_ENV === "development" && (
				<div className="fixed bottom-4 right-4 bg-black/80 text-white p-3 rounded-lg text-xs font-mono z-50">
					<div>Inactivity Debug:</div>
					<div>Active: {inactivityState.isActive ? "✅" : "❌"}</div>
					<div>Idle: {inactivityState.isIdle ? "⚠️" : "✅"}</div>
					<div>Warning: {inactivityState.isWarning ? "🚨" : "✅"}</div>
					<div>
						Time Remaining: {Math.ceil(inactivityState.timeRemaining / 1000)}s
					</div>
					<div>
						Last Activity:{" "}
						{new Date(inactivityState.lastActivity).toLocaleTimeString()}
					</div>
				</div>
			)}
		</>
	);
}

/**
 * Simple wrapper component for easier integration
 * Use this if you just want to add inactivity timeout to your app
 */
export function SimpleInactivityTimeout() {
	return <InactivityTimeoutProvider />;
}

/**
 * Hook to access inactivity timeout state from anywhere in the app
 * This is useful for components that want to display timeout status
 * or trigger manual activity resets
 */
export function useInactivityStatus() {
	const { isAuthenticated } = useAuth();
	const config = useInactivityConfig();

	return {
		isEnabled: config.enabled && isAuthenticated,
		config,
	};
}
